import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Button,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Typography,
  TextField,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  useMediaQuery,
  CircularProgress,
  Snackbar,
  IconButton,
  Avatar,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import SaveIcon from '@mui/icons-material/Save';
import PublishIcon from '@mui/icons-material/Publish';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import api from '../api/client';

// Każdy szablon dostaje własny, dedykowany krok (patrz TEMPLATE_STEP) — reszta
// kroków (Podstawy / Harmonogram / Uczestnicy / Nagrody / Promocja) jest
// wspólna dla wszystkich typów kampanii.
const TEMPLATE_META = {
  'steps-challenge': { stepLabel: 'Wymagania i cel', category: 'health', requiresMobileApp: true, color: '#00c875' },
  'qr-event': { stepLabel: 'Miejsce i limity', category: 'event', requiresMobileApp: false, color: '#579bfc' },
  'contest': { stepLabel: 'Zgłoszenia i głosowanie', category: 'contest', requiresMobileApp: false, color: '#ff3d57' },
  'quiz': { stepLabel: 'Pytania', category: 'education', requiresMobileApp: false, color: '#ffcc00' },
  'volunteering': { stepLabel: 'Zapisy', category: 'csr', requiresMobileApp: false, color: '#6c6cff' },
  'custom': { stepLabel: 'Zasady', category: 'other', requiresMobileApp: false, color: '#6D55F7' },
};

const CATEGORY_OPTIONS = [
  { value: 'health', label: 'Zdrowie i aktywność' },
  { value: 'event', label: 'Wydarzenie firmowe' },
  { value: 'contest', label: 'Konkurs' },
  { value: 'education', label: 'Edukacja' },
  { value: 'csr', label: 'CSR / wolontariat' },
  { value: 'other', label: 'Inne' },
];

const COVER_SWATCHES = [
  { id: 'violet', color: '#6D55F7' },
  { id: 'lime', color: '#9DE94B' },
  { id: 'orange', color: '#F47B35' },
  { id: 'blue', color: '#579bfc' },
  { id: 'pink', color: '#ff3d57' },
  { id: 'gold', color: '#ffcc00' },
];

const GROUP_OPTIONS = ['HR', 'Sales', 'IT', 'Marketing', 'Operations', 'All'];

function CampaignWizard() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get('template') || 'custom';
  const meta = TEMPLATE_META[templateId] || TEMPLATE_META.custom;
  const isStepsChallenge = templateId === 'steps-challenge';
  const isQrEvent = templateId === 'qr-event';
  const isContest = templateId === 'contest';
  const isQuiz = templateId === 'quiz';
  const isVolunteering = templateId === 'volunteering';

  const STEPS = ['Podstawy', 'Harmonogram', 'Uczestnicy', meta.stepLabel, 'Nagrody i załączniki', 'Promocja'];

  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState({
    title: isStepsChallenge ? 'Wyzwanie krokowe' : '',
    description: isStepsChallenge ? 'Rób kroki każdego dnia i wspinaj się w rankingu firmy!' : '',
    category: meta.category,
    coverImageUrl: '',
    coverSwatch: meta.color,
    logoUrl: '',
    hasRegistrationWindow: true,
    registrationStart: dayjs(),
    registrationEnd: dayjs().add(1, 'day').startOf('day'),
    startsAt: dayjs().add(1, 'day').startOf('day'),
    endsAt: dayjs().add(8, 'day').endOf('day'),
    hasDissolutionWindow: true,
    dissolutionEnd: dayjs().add(11, 'day').endOf('day'),
    targetGroups: [],
    inviteEmails: [],
    inviteInput: '',
    // steps-challenge
    goalType: 'individual',
    goalValue: isStepsChallenge ? 100000 : 10000,
    dailyLimit: 15000,
    // qr-event
    location: '',
    capacity: 50,
    // contest
    submissionFormat: 'Zdjęcie',
    submissionDeadline: dayjs().add(5, 'day'),
    moderationRequired: true,
    votingEnabled: true,
    // quiz
    questions: [{ text: '', points: 10 }],
    // volunteering
    confirmationMethod: 'self-report',
    // rewards & attachments
    reward: '',
    rewardWinners: 3,
    attachments: [],
    // promotion
    autoPublish: true,
    sendEmail: true,
    sendPush: false,
    webhook: true,
  });

  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const setValue = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const addInviteEmail = () => {
    const email = form.inviteInput.trim();
    if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && !form.inviteEmails.includes(email)) {
      setForm((prev) => ({ ...prev, inviteEmails: [...prev.inviteEmails, email], inviteInput: '' }));
    } else {
      setForm((prev) => ({ ...prev, inviteInput: '' }));
    }
  };
  const removeInviteEmail = (email) =>
    setForm((prev) => ({ ...prev, inviteEmails: prev.inviteEmails.filter((e) => e !== email) }));

  const addQuestion = () =>
    setForm((prev) => ({ ...prev, questions: [...prev.questions, { text: '', points: 10 }] }));
  const removeQuestion = (idx) =>
    setForm((prev) => ({ ...prev, questions: prev.questions.filter((_, i) => i !== idx) }));
  const setQuestion = (idx, field, value) =>
    setForm((prev) => ({
      ...prev,
      questions: prev.questions.map((q, i) => (i === idx ? { ...q, [field]: value } : q)),
    }));

  const addAttachment = () =>
    setForm((prev) => ({ ...prev, attachments: [...prev.attachments, { name: '', url: '' }] }));
  const removeAttachment = (idx) =>
    setForm((prev) => ({ ...prev, attachments: prev.attachments.filter((_, i) => i !== idx) }));
  const setAttachment = (idx, field, value) =>
    setForm((prev) => ({
      ...prev,
      attachments: prev.attachments.map((a, i) => (i === idx ? { ...a, [field]: value } : a)),
    }));

  const buildPayload = () => {
    const rules = {
      challengeType: isStepsChallenge ? 'steps' : templateId,
      goalType: form.goalType,
      goalValue: form.goalValue,
      dailyLimit: form.dailyLimit,
      targetGroups: form.targetGroups,
      location: isQrEvent ? form.location : undefined,
      capacity: isQrEvent || isVolunteering ? form.capacity : undefined,
      submissionFormat: isContest ? form.submissionFormat : undefined,
      submissionDeadline: isContest ? form.submissionDeadline?.toISOString() : undefined,
      moderationRequired: isContest ? form.moderationRequired : undefined,
      votingEnabled: isContest ? form.votingEnabled : undefined,
      questions: isQuiz ? form.questions.filter((q) => q.text.trim()) : undefined,
      confirmationMethod: isVolunteering ? form.confirmationMethod : undefined,
      attachments: form.attachments.filter((a) => a.url.trim()),
      communication: { autoPublish: form.autoPublish, sendEmail: form.sendEmail, sendPush: form.sendPush, webhook: form.webhook },
    };
    return {
      title: form.title,
      description: form.description,
      templateId,
      category: form.category,
      challengeType: isStepsChallenge ? 'steps' : templateId,
      requiresMobileApp: meta.requiresMobileApp,
      coverImageUrl: form.coverImageUrl || null,
      logoUrl: form.logoUrl || null,
      registrationStart: form.hasRegistrationWindow ? form.registrationStart?.toISOString() : null,
      registrationEnd: form.hasRegistrationWindow ? form.registrationEnd?.toISOString() : null,
      startDate: form.startsAt?.toISOString(),
      endDate: form.endsAt?.toISOString(),
      dissolutionEnd: form.hasDissolutionWindow ? form.dissolutionEnd?.toISOString() : null,
      rules,
      rewards: [{ place: form.rewardWinners > 1 ? `1-${form.rewardWinners}` : '1', prize: form.reward }],
    };
  };

  const sendDedicatedInvites = async (campaignId) => {
    if (form.inviteEmails.length === 0) return;
    try {
      await api.post(`/api/campaigns/${campaignId}/invites`, {
        recipients: form.inviteEmails.map((email) => ({ email })),
      });
    } catch (err) {
      console.error('Nie udało się wysłać zaproszeń:', err);
    }
  };

  const handleNext = async (e) => {
    e.preventDefault();
    if (activeStep === STEPS.length - 1) {
      setSaving(true);
      try {
        const { id } = await api.post('/api/campaigns', buildPayload());
        await api.post(`/api/campaigns/${id}/publish`);
        await sendDedicatedInvites(id);
        setSnackbar({ open: true, message: 'Kampania opublikowana!', severity: 'success' });
        setTimeout(() => navigate(`/campaigns/${id}`), 1200);
      } catch (err) {
        setSnackbar({ open: true, message: `Błąd: ${err.body?.error || err.message}`, severity: 'error' });
      } finally {
        setSaving(false);
      }
    } else {
      setActiveStep((s) => s + 1);
    }
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      const payload = buildPayload();
      const { id } = await api.post('/api/campaigns', { ...payload, title: payload.title || 'Draft kampanii' });
      await sendDedicatedInvites(id);
      setSnackbar({ open: true, message: 'Draft zapisany', severity: 'success' });
      setTimeout(() => navigate('/campaigns'), 1200);
    } catch (err) {
      setSnackbar({ open: true, message: `Błąd: ${err.body?.error || err.message}`, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Nowa kampania
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Szablon: <b>{form.title || templateId}</b>
      </Typography>
      {meta.requiresMobileApp && (
        <Alert severity="info" icon={<PhoneIphoneIcon />} sx={{ mb: 3 }}>
          <b>Wymagana aplikacja mobilna.</b> Uczestnicy zobaczą kampanię w aplikacji mobilnej i muszą: (1) zainstalować
          aplikację, (2) udostępnić dane zdrowotne (Apple Health / Google Fit), (3) kliknąć „Weź udział” — dopiero od
          tego momentu ich aktywność liczy się do rankingu.
        </Alert>
      )}

      <Stepper
        activeStep={activeStep}
        orientation={isMobile ? 'vertical' : 'horizontal'}
        sx={{ mb: 4 }}
      >
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Card>
        <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
          <form onSubmit={handleNext}>
            <Stack spacing={3}>
              {activeStep === 0 && (
                <>
                  <TextField
                    label="Nazwa kampanii"
                    fullWidth
                    required
                    value={form.title}
                    onChange={(e) => setValue('title', e.target.value)}
                  />
                  <FormControl fullWidth>
                    <InputLabel id="category-label">Kategoria</InputLabel>
                    <Select
                      labelId="category-label"
                      value={form.category}
                      label="Kategoria"
                      onChange={(e) => setValue('category', e.target.value)}
                    >
                      {CATEGORY_OPTIONS.map((c) => (
                        <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label="Opis"
                    multiline
                    rows={3}
                    fullWidth
                    value={form.description}
                    onChange={(e) => setValue('description', e.target.value)}
                    helperText="Krótko wyjaśnij, na czym polega aktywność i co uczestnik ma zrobić."
                  />
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Okładka kampanii</Typography>
                    <Stack direction="row" spacing={1.5} sx={{ mb: 1.5 }}>
                      {COVER_SWATCHES.map((s) => (
                        <Avatar
                          key={s.id}
                          onClick={() => setValue('coverSwatch', s.color)}
                          sx={{
                            bgcolor: s.color,
                            width: 40,
                            height: 40,
                            cursor: 'pointer',
                            border: form.coverSwatch === s.color && !form.coverImageUrl ? `3px solid ${theme.palette.text.primary}` : '3px solid transparent',
                          }}
                        >
                          {' '}
                        </Avatar>
                      ))}
                    </Stack>
                    <TextField
                      label="Lub adres URL własnej okładki (opcjonalnie)"
                      fullWidth
                      size="small"
                      value={form.coverImageUrl}
                      onChange={(e) => setValue('coverImageUrl', e.target.value)}
                    />
                  </Box>
                  <TextField
                    label="Adres URL logo (opcjonalnie)"
                    fullWidth
                    size="small"
                    value={form.logoUrl}
                    onChange={(e) => setValue('logoUrl', e.target.value)}
                  />
                </>
              )}

              {activeStep === 1 && (
                <>
                  <Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.hasRegistrationWindow}
                          onChange={(e) => setValue('hasRegistrationWindow', e.target.checked)}
                        />
                      }
                      label="Okno zapisów (uczestnicy dołączają przed startem)"
                    />
                    {form.hasRegistrationWindow && (
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                        <DateTimePicker
                          label="Zapisy od"
                          value={form.registrationStart}
                          onChange={(v) => setValue('registrationStart', v)}
                          sx={{ flex: 1, minWidth: { xs: '100%', sm: 240 } }}
                        />
                        <DateTimePicker
                          label="Zapisy do"
                          value={form.registrationEnd}
                          onChange={(v) => setValue('registrationEnd', v)}
                          sx={{ flex: 1, minWidth: { xs: '100%', sm: 240 } }}
                        />
                      </Box>
                    )}
                  </Box>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Czas trwania kampanii</Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      <DateTimePicker
                        label="Start"
                        value={form.startsAt}
                        onChange={(v) => setValue('startsAt', v)}
                        sx={{ flex: 1, minWidth: { xs: '100%', sm: 240 } }}
                      />
                      <DateTimePicker
                        label="Koniec"
                        value={form.endsAt}
                        onChange={(v) => setValue('endsAt', v)}
                        sx={{ flex: 1, minWidth: { xs: '100%', sm: 240 } }}
                      />
                    </Box>
                  </Box>
                  <Divider />
                  <Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.hasDissolutionWindow}
                          onChange={(e) => setValue('hasDissolutionWindow', e.target.checked)}
                        />
                      }
                      label="Czas na rozwiązanie kampanii (wyniki, nagrody, rozliczenie)"
                    />
                    {form.hasDissolutionWindow && (
                      <DateTimePicker
                        label="Rozwiązanie do"
                        value={form.dissolutionEnd}
                        onChange={(v) => setValue('dissolutionEnd', v)}
                        sx={{ mt: 1, minWidth: { xs: '100%', sm: 240 } }}
                      />
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Status kampanii zmienia się automatycznie: Zaplanowana → Zapisy → Aktywna → Rozliczenie → Zakończona.
                    </Typography>
                  </Box>
                </>
              )}

              {activeStep === 2 && (
                <>
                  <FormControl fullWidth>
                    <InputLabel id="groups-label">Grupy / działy docelowe</InputLabel>
                    <Select
                      labelId="groups-label"
                      multiple
                      value={form.targetGroups}
                      onChange={(e) => setValue('targetGroups', e.target.value)}
                      input={<OutlinedInput label="Grupy / działy docelowe" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => (
                            <Chip key={value} label={value} />
                          ))}
                        </Box>
                      )}
                    >
                      {GROUP_OPTIONS.map((group) => (
                        <MenuItem key={group} value={group}>
                          {group}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Zaproszenia dedykowane</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Oprócz ogłoszenia dla całej grupy możesz zaprosić konkretne osoby e-mailem.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        label="Adres e-mail"
                        size="small"
                        fullWidth
                        value={form.inviteInput}
                        onChange={(e) => setValue('inviteInput', e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addInviteEmail(); } }}
                      />
                      <Button variant="outlined" startIcon={<AddIcon />} onClick={addInviteEmail}>Dodaj</Button>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1.5 }}>
                      {form.inviteEmails.map((email) => (
                        <Chip key={email} label={email} onDelete={() => removeInviteEmail(email)} />
                      ))}
                    </Box>
                  </Box>
                </>
              )}

              {activeStep === 3 && isStepsChallenge && (
                <>
                  <FormControl fullWidth>
                    <InputLabel id="goal-label">Typ celu</InputLabel>
                    <Select
                      labelId="goal-label"
                      value={form.goalType}
                      label="Typ celu"
                      onChange={(e) => setValue('goalType', e.target.value)}
                    >
                      <MenuItem value="individual">Indywidualny</MenuItem>
                      <MenuItem value="team">Zespołowy</MenuItem>
                      <MenuItem value="company">Cała firma</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="Cel kroków (na uczestnika)"
                    type="number"
                    fullWidth
                    value={form.goalValue}
                    onChange={(e) => setValue('goalValue', Number(e.target.value))}
                    helperText="Łączna liczba kroków do zebrania w trakcie wyzwania."
                  />
                  <TextField
                    label="Dzienny limit kroków"
                    type="number"
                    fullWidth
                    value={form.dailyLimit}
                    onChange={(e) => setValue('dailyLimit', Number(e.target.value))}
                    helperText="Maksymalna liczba kroków dziennie liczona do rankingu (ochrona przed nadużyciami)."
                  />
                </>
              )}

              {activeStep === 3 && isQrEvent && (
                <>
                  <Alert severity="info">Uczestnicy potwierdzają obecność, skanując kod QR na miejscu wydarzenia.</Alert>
                  <TextField
                    label="Miejsce wydarzenia"
                    fullWidth
                    value={form.location}
                    onChange={(e) => setValue('location', e.target.value)}
                  />
                  <TextField
                    label="Limit miejsc"
                    type="number"
                    fullWidth
                    value={form.capacity}
                    onChange={(e) => setValue('capacity', Number(e.target.value))}
                    helperText="Zapisy zostaną zamknięte po osiągnięciu limitu."
                  />
                </>
              )}

              {activeStep === 3 && isContest && (
                <>
                  <FormControl fullWidth>
                    <InputLabel id="format-label">Format zgłoszenia</InputLabel>
                    <Select
                      labelId="format-label"
                      value={form.submissionFormat}
                      label="Format zgłoszenia"
                      onChange={(e) => setValue('submissionFormat', e.target.value)}
                    >
                      <MenuItem value="Zdjęcie">Zdjęcie</MenuItem>
                      <MenuItem value="Wideo">Wideo</MenuItem>
                      <MenuItem value="Tekst">Tekst</MenuItem>
                    </Select>
                  </FormControl>
                  <DateTimePicker
                    label="Termin zgłoszeń"
                    value={form.submissionDeadline}
                    onChange={(v) => setValue('submissionDeadline', v)}
                    sx={{ minWidth: { xs: '100%', sm: 240 } }}
                  />
                  <FormControlLabel
                    control={<Switch checked={form.moderationRequired} onChange={(e) => setValue('moderationRequired', e.target.checked)} />}
                    label="Moderacja zgłoszeń przed publikacją"
                  />
                  <FormControlLabel
                    control={<Switch checked={form.votingEnabled} onChange={(e) => setValue('votingEnabled', e.target.checked)} />}
                    label="Głosowanie uczestników na zwycięzcę"
                  />
                </>
              )}

              {activeStep === 3 && isQuiz && (
                <>
                  <Typography variant="body2" color="text.secondary">Zdefiniuj pytania i liczbę punktów za poprawną odpowiedź.</Typography>
                  {form.questions.map((q, idx) => (
                    <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                      <TextField
                        label={`Pytanie ${idx + 1}`}
                        fullWidth
                        value={q.text}
                        onChange={(e) => setQuestion(idx, 'text', e.target.value)}
                      />
                      <TextField
                        label="Punkty"
                        type="number"
                        sx={{ width: 120 }}
                        value={q.points}
                        onChange={(e) => setQuestion(idx, 'points', Number(e.target.value))}
                      />
                      <IconButton onClick={() => removeQuestion(idx)} disabled={form.questions.length === 1}>
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Box>
                  ))}
                  <Button startIcon={<AddIcon />} onClick={addQuestion} sx={{ alignSelf: 'flex-start' }}>Dodaj pytanie</Button>
                </>
              )}

              {activeStep === 3 && isVolunteering && (
                <>
                  <Alert severity="success">Ta aktywność nie wymaga aplikacji mobilnej — udział potwierdza się ręcznie.</Alert>
                  <TextField
                    label="Limit uczestników"
                    type="number"
                    fullWidth
                    value={form.capacity}
                    onChange={(e) => setValue('capacity', Number(e.target.value))}
                  />
                  <FormControl fullWidth>
                    <InputLabel id="confirm-label">Sposób potwierdzenia udziału</InputLabel>
                    <Select
                      labelId="confirm-label"
                      value={form.confirmationMethod}
                      label="Sposób potwierdzenia udziału"
                      onChange={(e) => setValue('confirmationMethod', e.target.value)}
                    >
                      <MenuItem value="self-report">Samodzielne zgłoszenie</MenuItem>
                      <MenuItem value="qr">Kod QR na miejscu</MenuItem>
                      <MenuItem value="manager-approval">Zatwierdzenie przez managera</MenuItem>
                    </Select>
                  </FormControl>
                </>
              )}

              {activeStep === 3 && !isStepsChallenge && !isQrEvent && !isContest && !isQuiz && !isVolunteering && (
                <>
                  <FormControl fullWidth>
                    <InputLabel id="goal-label-custom">Typ celu</InputLabel>
                    <Select
                      labelId="goal-label-custom"
                      value={form.goalType}
                      label="Typ celu"
                      onChange={(e) => setValue('goalType', e.target.value)}
                    >
                      <MenuItem value="individual">Indywidualny</MenuItem>
                      <MenuItem value="team">Zespołowy</MenuItem>
                      <MenuItem value="company">Cała firma</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="Wartość celu"
                    type="number"
                    fullWidth
                    value={form.goalValue}
                    onChange={(e) => setValue('goalValue', Number(e.target.value))}
                  />
                </>
              )}

              {activeStep === 4 && (
                <>
                  <TextField
                    label="Opis nagród"
                    multiline
                    rows={2}
                    fullWidth
                    value={form.reward}
                    onChange={(e) => setValue('reward', e.target.value)}
                    helperText="Np. „Bon o wartości 100 PLN dla 3 najlepszych uczestników”."
                  />
                  <TextField
                    label="Liczba zwycięzców"
                    type="number"
                    fullWidth
                    value={form.rewardWinners}
                    onChange={(e) => setValue('rewardWinners', Number(e.target.value))}
                  />
                  <Divider />
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Załączniki</Typography>
                    {form.attachments.map((a, idx) => (
                      <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <TextField
                          label="Nazwa"
                          size="small"
                          value={a.name}
                          onChange={(e) => setAttachment(idx, 'name', e.target.value)}
                          sx={{ flex: 1 }}
                        />
                        <TextField
                          label="Adres URL"
                          size="small"
                          value={a.url}
                          onChange={(e) => setAttachment(idx, 'url', e.target.value)}
                          sx={{ flex: 2 }}
                        />
                        <IconButton onClick={() => removeAttachment(idx)}><DeleteOutlineIcon /></IconButton>
                      </Box>
                    ))}
                    <Button startIcon={<AddIcon />} onClick={addAttachment} size="small">Dodaj załącznik</Button>
                  </Box>
                </>
              )}

              {activeStep === 5 && (
                <>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.autoPublish}
                        onChange={(e) => setValue('autoPublish', e.target.checked)}
                      />
                    }
                    label="Automatycznie opublikuj post na firmowym Wallu przez webhook"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.sendEmail}
                        onChange={(e) => setValue('sendEmail', e.target.checked)}
                      />
                    }
                    label="Wyślij zaproszenia e-mailem"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.sendPush}
                        onChange={(e) => setValue('sendPush', e.target.checked)}
                      />
                    }
                    label="Wyślij push powiadomienia"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.webhook}
                        onChange={(e) => setValue('webhook', e.target.checked)}
                      />
                    }
                    label="Aktywuj webhook challenge.published"
                  />
                  <Divider />
                  <Alert severity="info">
                    Podgląd: kampania zostanie opublikowana dla wybranych grup.
                  </Alert>
                </>
              )}

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  gap: 1.5,
                  pt: 2,
                }}
              >
                <Button
                  disabled={activeStep === 0}
                  onClick={() => setActiveStep((s) => s - 1)}
                  sx={{ order: { xs: 3, sm: 1 }, width: { xs: '100%', sm: 'auto' } }}
                >
                  Wstecz
                </Button>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 1.5,
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  <Button
                    variant="outlined"
                    startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                    onClick={handleSaveDraft}
                    disabled={saving}
                    sx={{ order: { xs: 2, sm: 1 }, width: { xs: '100%', sm: 'auto' } }}
                  >
                    Zapisz draft
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={saving}
                    startIcon={saving && activeStep === STEPS.length - 1 ? <CircularProgress size={16} color="inherit" /> : <PublishIcon />}
                    sx={{ order: { xs: 1, sm: 2 }, width: { xs: '100%', sm: 'auto' } }}
                  >
                    {activeStep === STEPS.length - 1 ? 'Opublikuj kampanię' : 'Dalej'}
                  </Button>
                </Box>
              </Box>
            </Stack>
          </form>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default CampaignWizard;
